import { aimooT } from "@utils/aimoo-i18n";
import { editRegistrationApplication, setIsoData } from "@utils/app";
import {
  getQueryParams,
  getQueryString,
  resourcesSettled,
} from "@utils/helpers";
import { scrollMixin } from "../mixins/scroll-mixin";
import {
  ItemIdAndRes,
  itemLoading,
  QueryParams,
  RouteDataResponse,
} from "@utils/types";
import { Component, InfernoNode } from "inferno";
import {
  ApproveRegistrationApplication,
  LemmyHttp,
  PagedResponse,
  PaginationCursor,
  RegistrationApplicationId,
  RegistrationApplicationResponse,
  RegistrationApplicationView,
} from "lemmy-js-client";
import { fetchLimit } from "@utils/config";
import { InitialFetchRequest } from "@utils/types";
import { FirstLoadService, I18NextService } from "../../services";
import {
  EMPTY_REQUEST,
  HttpService,
  LOADING_REQUEST,
  RequestState,
  wrapClient,
} from "../../services/HttpService";
import { HtmlTags } from "../common/html-tags";
import { Icon, Spinner } from "../common/icon";
import { RegistrationApplication } from "../common/registration-application";
import { getHttpBaseInternal } from "../../utils/env";
import { isBrowser } from "@utils/browser";
import { PaginatorCursor } from "@components/common/paginator-cursor";
import { Link, RouteComponentProps, RouterContext } from "inferno-router";
import { IRoutePropsWithFetch } from "@utils/routes";
import {
  RegistrationState,
  RegistrationStateDropdown,
} from "@components/common/registration-state-dropdown";
import { removeLocalStorageMarkdown } from "@components/common/markdown-textarea";
import classNames from "classnames";

type RegistrationApplicationsData = RouteDataResponse<{
  listRegistrationApplicationsResponse: PagedResponse<RegistrationApplicationView>;
}>;

interface RegistrationApplicationsState {
  appsRes: RequestState<PagedResponse<RegistrationApplicationView>>;
  approveRes: ItemIdAndRes<
    RegistrationApplicationId,
    RegistrationApplicationResponse
  >;
  isIsomorphic: boolean;
}

interface RegistrationApplicationsProps {
  view: RegistrationState;
  cursor?: PaginationCursor;
}

function registrationStateFromQuery(view?: string): RegistrationState {
  switch (view) {
    case "unread":
    case "all":
    case "denied":
      return view;
    default:
      return "unread";
  }
}

export function getRegistrationApplicationQueryParams(
  source?: string,
): RegistrationApplicationsProps {
  return getQueryParams<RegistrationApplicationsProps>(
    {
      view: registrationStateFromQuery,
      cursor: (cursor?: string) => cursor,
    },
    source,
  );
}

export type RegistrationApplicationsFetchConfig = IRoutePropsWithFetch<
  RegistrationApplicationsData,
  Record<string, never>,
  RegistrationApplicationsProps
>;

type RegistrationApplicationsRouteProps = RouteComponentProps<
  Record<string, never>
> &
  RegistrationApplicationsProps;

@scrollMixin
export class RegistrationApplications extends Component<
  RegistrationApplicationsRouteProps,
  RegistrationApplicationsState
> {
  private isoData = setIsoData<RegistrationApplicationsData>(this.context);
  state: RegistrationApplicationsState = {
    appsRes: EMPTY_REQUEST,
    approveRes: { id: 0, res: LOADING_REQUEST },
    isIsomorphic: false,
  };

  loadingSettled() {
    return resourcesSettled([this.state.appsRes]);
  }

  constructor(props: RegistrationApplicationsRouteProps, context: object) {
    super(props, context);

    // Only fetch the data if coming from another route
    if (FirstLoadService.isFirstLoad) {
      this.state = {
        ...this.state,
        appsRes: this.isoData.routeData.listRegistrationApplicationsResponse,
        isIsomorphic: true,
      };
    }
  }

  async componentWillMount() {
    if (!this.state.isIsomorphic && isBrowser()) {
      await this.refetch(this.props);
    }
  }

  async componentWillReceiveProps(
    nextProps: RegistrationApplicationsRouteProps & { children?: InfernoNode },
  ) {
    if (
      nextProps.view !== this.props.view ||
      nextProps.cursor !== this.props.cursor
    ) {
      await this.refetch(nextProps);
    }
  }

  get documentTitle(): string {
    const mui = this.isoData.myUserInfo;
    return mui
      ? `@${mui.local_user_view.person.name} ${I18NextService.i18n.t(
          "registration_applications",
        )} - ${this.isoData.siteRes.site_view.site.name}`
      : "";
  }

  get filteredApps(): RegistrationApplicationView[] | undefined {
    if (this.state.appsRes.state !== "success") {
      return undefined;
    }
    let apps = this.state.appsRes.data.items;
    if (this.props.view === "denied") {
      apps = apps.filter(ra => !ra.creator_local_user.accepted_application);
    }
    return apps;
  }

  render() {
    const apps = this.filteredApps;
    const appsState = this.state.appsRes.state;
    const count = apps?.length;

    return (
      <div className="aimoo-reg-apps registration-applications">
        <HtmlTags
          title={this.documentTitle}
          context={this.context as RouterContext}
        />
        <div className="aimoo-reg-apps__inner container-lg px-3">
          <nav className="aimoo-reg-apps__breadcrumb" aria-label="breadcrumb">
            <Link to="/" className="aimoo-reg-apps__breadcrumb-link">
              {aimooT("community")}
            </Link>
            <span className="aimoo-reg-apps__breadcrumb-sep" aria-hidden="true">
              /
            </span>
            <span className="aimoo-reg-apps__breadcrumb-current">
              {aimooT("reg_apps_title")}
            </span>
          </nav>

          <header className="aimoo-reg-apps__header">
            <div>
              <h1 className="aimoo-reg-apps__title">
                {aimooT("reg_apps_title")}
              </h1>
              <p className="aimoo-reg-apps__subtitle">
                {aimooT("reg_apps_subtitle")}
              </p>
            </div>
            {typeof count === "number" && (
              <span
                className={classNames("aimoo-reg-apps__count-badge", {
                  "aimoo-reg-apps__count-badge--empty": count === 0,
                  "aimoo-reg-apps__count-badge--denied":
                    this.props.view === "denied" && count > 0,
                })}
              >
                {viewCountLabel(this.props.view, count)}
              </span>
            )}
          </header>

          <div className="aimoo-reg-apps__layout">
            <section className="aimoo-reg-apps__main">
              <div className="aimoo-reg-apps__toolbar">
                <div className="aimoo-reg-apps__filters">
                  <RegistrationStateDropdown
                    currentOption={this.props.view}
                    onSelect={val => handleRegistrationStateChange(this, val)}
                  />
                </div>
              </div>

              {appsState === "loading" && (
                <div className="aimoo-reg-apps__loading">
                  <Spinner large />
                  <span>{aimooT("loading_apps")}</span>
                </div>
              )}

              {apps && apps.length === 0 && (
                <div className="aimoo-reg-apps__empty">
                  <Icon icon="check" classes="icon-inline" />
                  <p className="aimoo-reg-apps__empty-title">
                    {aimooT("no_apps")}
                  </p>
                  <p className="aimoo-reg-apps__empty-desc">
                    {this.props.view === "unread"
                      ? aimooT("no_pending_apps")
                      : aimooT("try_other_filter")}
                  </p>
                </div>
              )}

              {apps && apps.length > 0 && (
                <div className="aimoo-reg-apps__cards">
                  {apps.map(ra => (
                    <article
                      key={ra.registration_application.id}
                      className={classNames("aimoo-reg-apps__card", {
                        "aimoo-reg-apps__card--approved":
                          ra.creator_local_user.accepted_application &&
                          !!ra.admin,
                        "aimoo-reg-apps__card--denied":
                          !ra.creator_local_user.accepted_application &&
                          !!ra.admin,
                        "aimoo-reg-apps__card--pending": !ra.admin,
                      })}
                    >
                      <div className="aimoo-reg-apps__card-top">
                        <span className="aimoo-reg-apps__type-badge">
                          {aimooT("reg_app_badge")}
                        </span>
                        <span
                          className={classNames("aimoo-reg-apps__status-badge", {
                            "aimoo-reg-apps__status-badge--approved":
                              ra.creator_local_user.accepted_application &&
                              !!ra.admin,
                            "aimoo-reg-apps__status-badge--denied":
                              !ra.creator_local_user.accepted_application &&
                              !!ra.admin,
                          })}
                        >
                          {statusLabel(ra)}
                        </span>
                      </div>
                      <RegistrationApplication
                        application={ra}
                        onApproveApplication={form =>
                          handleApproveApplication(this, form)
                        }
                        loading={
                          itemLoading(this.state.approveRes) ===
                          ra.registration_application.id
                        }
                        myUserInfo={this.isoData.myUserInfo}
                      />
                    </article>
                  ))}
                </div>
              )}

              {apps && (
                <div className="aimoo-reg-apps__pager">
                  <PaginatorCursor
                    current={this.props.cursor}
                    resource={this.state.appsRes}
                    onPageChange={cursor => handlePageChange(this, cursor)}
                  />
                </div>
              )}
            </section>

            <aside className="aimoo-reg-apps__aside">
              {this.renderTipsCard()}
              {this.renderProcessCard()}
            </aside>
          </div>
        </div>
      </div>
    );
  }

  renderTipsCard(): InfernoNode {
    return (
      <div className="aimoo-reg-apps__side-card aimoo-reg-apps__side-card--note">
        <div className="aimoo-reg-apps__side-note-head">
          <Icon icon="alert-triangle" classes="icon-inline" />
          <h2 className="aimoo-reg-apps__side-title">
            {aimooT("review_tips_title")}
          </h2>
        </div>
        <ul className="aimoo-reg-apps__tips">
          <li>{aimooT("review_tip_1")}</li>
          <li>{aimooT("review_tip_2")}</li>
          <li>{aimooT("review_tip_3")}</li>
          <li>{aimooT("review_tip_4")}</li>
        </ul>
      </div>
    );
  }

  renderProcessCard(): InfernoNode {
    const steps = [
      {
        title: aimooT("review_flow_1_title"),
        desc: aimooT("review_flow_1_desc"),
      },
      {
        title: aimooT("review_flow_2_title"),
        desc: aimooT("review_flow_2_desc"),
      },
      {
        title: aimooT("review_flow_3_title"),
        desc: aimooT("review_flow_3_desc"),
      },
      {
        title: aimooT("review_flow_4_title"),
        desc: aimooT("review_flow_4_desc"),
      },
    ];
    return (
      <div className="aimoo-reg-apps__side-card">
        <h2 className="aimoo-reg-apps__side-title">
          {aimooT("review_flow_title")}
        </h2>
        <ol className="aimoo-reg-apps__steps">
          {steps.map((step, index) => (
            <li key={step.title}>
              <span className="aimoo-reg-apps__step-num">{index + 1}</span>
              <span className="aimoo-reg-apps__step-text">
                <span className="aimoo-reg-apps__step-title">{step.title}</span>
                <span className="aimoo-reg-apps__step-desc">{step.desc}</span>
              </span>
            </li>
          ))}
        </ol>
      </div>
    );
  }

  static fetchInitialData = async ({
    headers,
    match: {
      params: { view, cursor },
    },
  }: InitialFetchRequest<
    Record<string, never>,
    RegistrationApplicationsProps
  >): Promise<RegistrationApplicationsData> => {
    const client = wrapClient(
      new LemmyHttp(getHttpBaseInternal(), { headers }),
    );
    return {
      listRegistrationApplicationsResponse: headers["Authorization"]
        ? await client.listRegistrationApplications({
            unread_only: view === "unread",
            page_cursor: cursor,
            limit: fetchLimit,
          })
        : EMPTY_REQUEST,
    };
  };

  refetchToken?: symbol;
  async refetch(props: RegistrationApplicationsProps) {
    const token = (this.refetchToken = Symbol());
    const { view: state, cursor } = props;
    this.setState({
      appsRes: LOADING_REQUEST,
    });
    const appsRes = await HttpService.client.listRegistrationApplications({
      unread_only: state === "unread",
      page_cursor: cursor,
      limit: fetchLimit,
    });
    if (token === this.refetchToken) {
      this.setState({ appsRes });
    }
  }

  updateUrl(props: Partial<RegistrationApplicationsProps>) {
    const { cursor, view: state } = { ...this.props, ...props };

    const queryParams: QueryParams<RegistrationApplicationsProps> = {
      cursor,
      view: state,
    };

    this.props.history.push(
      `/registration_applications${getQueryString(queryParams)}`,
    );
  }
}

function viewCountLabel(view: RegistrationState, count: number): string {
  switch (view) {
    case "unread":
      return aimooT("pending_review_count", { count });
    case "denied":
      return aimooT("rejected_count", { count });
    default:
      return aimooT("page_count", { count });
  }
}

function statusLabel(ra: RegistrationApplicationView): string {
  if (!ra.admin) {
    return aimooT("pending_review");
  }
  return ra.creator_local_user.accepted_application
    ? aimooT("approved")
    : aimooT("rejected");
}

function handleRegistrationStateChange(
  i: RegistrationApplications,
  val: RegistrationState,
) {
  i.updateUrl({ view: val, cursor: undefined });
}

function handlePageChange(
  i: RegistrationApplications,
  cursor?: PaginationCursor,
) {
  i.updateUrl({ cursor });
}

async function handleApproveApplication(
  i: RegistrationApplications,
  form: ApproveRegistrationApplication,
) {
  i.setState({ approveRes: { id: form.id, res: LOADING_REQUEST } });
  const res = await HttpService.client.approveRegistrationApplication(form);
  i.setState({ approveRes: { id: form.id, res } });

  i.setState(s => {
    if (s.appsRes.state === "success" && res.state === "success") {
      removeLocalStorageMarkdown();
      s.appsRes.data.items = editRegistrationApplication(
        res.data.registration_application,
        s.appsRes.data.items,
      );
    }
    return s;
  });
}
